import {useState} from 'react';
import {
    Accordion,
    Alert,
    Badge,
    Card,
    Code,
    Collapse,
    Divider,
    Grid,
    Group,
    SimpleGrid,
    Stack,
    Text,
    Timeline,
    Title,
    Tooltip,
    UnstyledButton,
} from '@mantine/core';
import {
    AiOutlineBuild,
    AiOutlineCheckCircle,
    AiOutlineCloudUpload,
    AiOutlineCode,
    AiOutlineExclamationCircle,
    AiOutlineExperiment,
    AiOutlineFileText,
    AiOutlineInbox,
    AiOutlineInfoCircle,
} from 'react-icons/ai';
import {
    AnalysisFinding,
    AnalysisRecommendation,
    FindingSeverity,
    PipelineStage,
    RepositoryAnalysis,
} from '../types';

const confidenceColor = (confidence: string) => {
    switch (confidence) {
        case 'HIGH':
            return 'green';
        case 'MEDIUM':
            return 'yellow';
        case 'LOW':
            return 'red';
        default:
            return 'gray';
    }
};

const confidenceHelp: Record<string, string> = {
    HIGH: 'Strong evidence across multiple files. Suggestions are likely correct as-is.',
    MEDIUM: 'Partial evidence. Review suggestions carefully before provisioning.',
    LOW: 'Limited evidence. Manual review and adjustments strongly recommended.',
};

const severityColor = (severity: FindingSeverity) =>
    severity === 'WARNING' ? 'yellow' : 'blue';

const stageKindIcon = (kind?: string) => {
    switch (kind) {
        case 'UNIT_TEST':
            return <AiOutlineExperiment/>;
        case 'BUILD':
            return <AiOutlineBuild/>;
        case 'ASSETS':
            return <AiOutlineInbox/>;
        case 'DEPLOY':
            return <AiOutlineCloudUpload/>;
        default:
            return <AiOutlineCode/>;
    }
};

const categorizeFile = (file: string): string => {
    const lower = file.toLowerCase();
    if (lower.includes('buildspec') || lower.includes('codebuild')) return 'Build';
    if (lower.includes('cdk.json') || lower.includes('cloudformation') || lower.endsWith('.template.json') || lower.endsWith('.template.yaml')) return 'Infrastructure';
    if (lower === 'package.json' || lower === 'pnpm-lock.yaml' || lower === 'yarn.lock' || lower === 'package-lock.json') return 'Package';
    if (lower.endsWith('.yml') || lower.endsWith('.yaml')) return 'Configuration';
    if (lower === 'dockerfile' || lower.startsWith('dockerfile.')) return 'Container';
    return 'Other';
};

const DetailRow = ({label, children}: { label: string; children: React.ReactNode }) => (
    <Stack gap={2}>
        <Text size='xs' c='dimmed' fw={500} tt='uppercase'>{label}</Text>
        <Text size='sm'>{children}</Text>
    </Stack>
);

const RecommendationsSection = ({items}: { items: AnalysisRecommendation[] }) => {
    if (!items.length) return null;

    const warnings = items.filter(i => i.severity === 'WARNING');
    const infos = items.filter(i => i.severity === 'INFO');
    const ordered = [...warnings, ...infos];

    return (
        <Stack gap='xs'>
            <Group gap='xs'>
                <Title order={5}>Recommendations</Title>
                <Badge size='sm' variant='light'>{items.length}</Badge>
            </Group>
            {ordered.map((rec) => (
                <Alert
                    key={rec.key}
                    color={severityColor(rec.severity)}
                    icon={rec.severity === 'WARNING' ? <AiOutlineExclamationCircle/> : <AiOutlineInfoCircle/>}
                    variant='light'
                >
                    <Stack gap='xs'>
                        <Text size='sm' fw={500}>{rec.message}</Text>
                        {rec.suggestion && (
                            <Text size='xs' c='dimmed'>{rec.suggestion}</Text>
                        )}
                    </Stack>
                </Alert>
            ))}
        </Stack>
    );
};

const FindingsSection = ({items}: { items: AnalysisFinding[] }) => {
    const [expanded, setExpanded] = useState(false);

    if (!items.length) return null;

    const grouped = items.reduce<Record<string, AnalysisFinding[]>>((acc, f) => {
        (acc[f.source] ||= []).push(f);
        return acc;
    }, {});

    const sources = Object.keys(grouped).sort();
    const hasWarnings = items.some(i => i.severity === 'WARNING');

    return (
        <Stack gap='xs'>
            <UnstyledButton onClick={() => setExpanded(e => !e)}>
                <Group gap='xs'>
                    <Title order={5}>Analysis evidence</Title>
                    <Badge size='sm' variant='light' color={hasWarnings ? 'yellow' : 'blue'}>
                        {items.length}
                    </Badge>
                    <Text size='xs' c='dimmed'>
                        {expanded ? 'Hide details' : 'Show details'}
                    </Text>
                </Group>
            </UnstyledButton>
            <Collapse in={expanded}>
                <Accordion variant='separated' multiple>
                    {sources.map((source) => (
                        <Accordion.Item key={source} value={source}>
                            <Accordion.Control>
                                <Group gap='xs'>
                                    <Text size='sm' fw={500}>{source}</Text>
                                    <Badge size='xs' variant='outline'>
                                        {grouped[source].length}
                                    </Badge>
                                </Group>
                            </Accordion.Control>
                            <Accordion.Panel>
                                <Stack gap='sm'>
                                    {grouped[source].map((f) => (
                                        <Stack key={f.key} gap={4}>
                                            <Group gap='xs'>
                                                <Badge
                                                    size='xs'
                                                    color={severityColor(f.severity)}
                                                    variant='light'
                                                >
                                                    {f.severity}
                                                </Badge>
                                                <Text size='sm' fw={500}>{f.label}</Text>
                                                {f.value && (
                                                    <Code>{f.value}</Code>
                                                )}
                                            </Group>
                                            {f.evidence && (
                                                <Text size='xs' c='dimmed' ml='md'>
                                                    {f.evidence}
                                                </Text>
                                            )}
                                        </Stack>
                                    ))}
                                </Stack>
                            </Accordion.Panel>
                        </Accordion.Item>
                    ))}
                </Accordion>
            </Collapse>
        </Stack>
    );
};

const StagesTimeline = ({stages}: { stages: PipelineStage[] }) => {
    const sorted = [...stages].sort((a, b) => a.order - b.order);

    return (
        <Stack gap='xs'>
            <Title order={5}>Suggested stages</Title>
            <Timeline active={sorted.length - 1} bulletSize={28} lineWidth={2}>
                {sorted.map((stage, i) => (
                    <Timeline.Item
                        key={i}
                        bullet={stageKindIcon(stage.stageKind)}
                        title={<Text fw={500}>{stage.name}</Text>}
                    >
                        <Group gap='xs' mt={4}>
                            {stage.stageKind && (
                                <Badge size='xs' variant='light'>{stage.stageKind}</Badge>
                            )}
                            {stage.buildProvider && (
                                <Badge size='xs' variant='outline' color='blue'>
                                    build: {stage.buildProvider}
                                </Badge>
                            )}
                            {stage.deployProvider && (
                                <Badge size='xs' variant='outline' color='grape'>
                                    deploy: {stage.deployProvider}
                                </Badge>
                            )}
                            {stage.requiresApproval && (
                                <Badge size='xs' color='yellow'>approval</Badge>
                            )}
                        </Group>
                    </Timeline.Item>
                ))}
            </Timeline>
        </Stack>
    );
};

const DeploymentAccountsGrid = ({accounts}: {
    accounts: NonNullable<RepositoryAnalysis['suggestedDeploymentAccounts']>
}) => (
    <Stack gap='xs'>
        <Title order={5}>Suggested deployment accounts</Title>
        <SimpleGrid cols={{base: 1, sm: 2}} spacing='xs'>
            {accounts.map((a, i) => (
                <Card key={i} withBorder padding='sm' radius='sm'>
                    <Stack gap={2}>
                        <Text size='sm' fw={500}>
                            {a.accountName ?? a.accountId}
                        </Text>
                        {a.accountName && (
                            <Text size='xs' c='dimmed' ff='monospace'>{a.accountId}</Text>
                        )}
                        {a.region && (
                            <Text size='xs' c='dimmed'>region: {a.region}</Text>
                        )}
                        {a.deploymentBucketName && (
                            <Text size='xs' c='dimmed' ff='monospace'>
                                bucket: {a.deploymentBucketName}
                            </Text>
                        )}
                    </Stack>
                </Card>
            ))}
        </SimpleGrid>
    </Stack>
);

const DetectedFilesSection = ({files}: { files: string[] }) => {
    const [expanded, setExpanded] = useState(false);

    const grouped = files.reduce<Record<string, string[]>>((acc, f) => {
        const cat = categorizeFile(f);
        (acc[cat] ||= []).push(f);
        return acc;
    }, {});

    const categories = Object.keys(grouped).sort();
    const shouldCollapse = files.length > 5;

    return (
        <Stack gap='xs'>
            <UnstyledButton onClick={() => shouldCollapse && setExpanded(e => !e)}>
                <Group gap='xs'>
                    <Title order={5}>
                        <Group gap={6} display='inline-flex'>
                            <AiOutlineFileText/>
                            Detected files
                        </Group>
                    </Title>
                    <Badge size='sm' variant='light'>{files.length}</Badge>
                    {shouldCollapse && (
                        <Text size='xs' c='dimmed'>
                            {expanded ? 'Collapse' : 'Expand'}
                        </Text>
                    )}
                </Group>
            </UnstyledButton>
            <Collapse in={!shouldCollapse || expanded}>
                <Stack gap='xs'>
                    {categories.map(cat => (
                        <Group key={cat} gap='xs' align='flex-start' wrap='wrap'>
                            <Badge size='sm' variant='outline' style={{minWidth: 110}}>
                                {cat}
                            </Badge>
                            <Group gap={6} wrap='wrap'>
                                {grouped[cat].map((f) => (
                                    <Code key={f}>{f}</Code>
                                ))}
                            </Group>
                        </Group>
                    ))}
                </Stack>
            </Collapse>
        </Stack>
    );
};

interface AnalysisResultPanelProps {
    analysis: RepositoryAnalysis;
}

export const AnalysisResultPanel = ({analysis}: AnalysisResultPanelProps) => {

    const findings = analysis.findings ?? [];
    const recommendations = analysis.recommendations ?? [];
    const stages = analysis.suggestedStages ?? [];
    const accounts = analysis.suggestedDeploymentAccounts ?? [];
    const files = analysis.detectedFiles ?? [];

    return (
        <Stack gap='lg'>
            {/* Header card: confidence + repo + suggested name */}
            <Card withBorder padding='md' radius='md'>
                <Group justify='space-between' align='flex-start' wrap='nowrap'>
                    <Stack gap={4}>
                        <Text size='xs' c='dimmed' tt='uppercase' fw={500}>
                            Suggested pipeline
                        </Text>
                        <Title order={3}>{analysis.suggestedPipelineName}</Title>
                        <Text size='sm' c='dimmed'>
                            {analysis.repositoryName} · {analysis.branchName}
                        </Text>
                    </Stack>
                    <Tooltip
                        label={confidenceHelp[analysis.confidence] ?? ''}
                        multiline
                        w={260}
                        withArrow
                    >
                        <Badge
                            size='lg'
                            color={confidenceColor(analysis.confidence)}
                            leftSection={<AiOutlineCheckCircle/>}
                        >
                            {analysis.confidence} confidence
                        </Badge>
                    </Tooltip>
                </Group>
            </Card>

            {/* Summary grid */}
            <Card withBorder padding='md' radius='md'>
                <Grid gutter='md'>
                    <Grid.Col span={{base: 12, sm: 6}}>
                        <DetailRow label='Pipeline type'>
                            <Badge variant='light'>{analysis.suggestedPipelineType}</Badge>
                        </DetailRow>
                    </Grid.Col>
                    {analysis.detectedArchetype && (
                        <Grid.Col span={{base: 12, sm: 6}}>
                            <DetailRow label='Detected archetype'>
                                <Badge variant='light' color='grape'>
                                    {analysis.detectedArchetype}
                                </Badge>
                            </DetailRow>
                        </Grid.Col>
                    )}
                    {analysis.suggestedTemplateName && (
                        <Grid.Col span={{base: 12, sm: 6}}>
                            <DetailRow label='Template'>
                                {analysis.suggestedTemplateName}
                            </DetailRow>
                        </Grid.Col>
                    )}
                    {analysis.suggestedBuildCommand && (
                        <Grid.Col span={12}>
                            <DetailRow label='Build command'>
                                <Code>{analysis.suggestedBuildCommand}</Code>
                            </DetailRow>
                        </Grid.Col>
                    )}
                </Grid>
            </Card>

            {recommendations.length > 0 && (
                <>
                    <RecommendationsSection items={recommendations}/>
                    <Divider/>
                </>
            )}

            {stages.length > 0 && (
                <>
                    <StagesTimeline stages={stages}/>
                    <Divider/>
                </>
            )}

            {accounts.length > 0 && (
                <>
                    <DeploymentAccountsGrid accounts={accounts}/>
                    <Divider/>
                </>
            )}

            {files.length > 0 && (
                <>
                    <DetectedFilesSection files={files}/>
                    {findings.length > 0 && <Divider/>}
                </>
            )}

            {findings.length > 0 && <FindingsSection items={findings}/>}
        </Stack>
    );
};
